/**
 * Current Technology Stack - structured model + hub-and-spoke landscape layout.
 *
 * The section moved from "one tool + one plan per category" to a repeatable list of
 * systems. Each system carries a post-implementation plan, an in/out-of-scope flag, and
 * (when in scope) an integration spec (type / hours / direction / notes). This one model
 * is the single source of truth consumed by the web editor, the estimate, the SOW, and
 * the Future State diagram.
 *
 * The diagram is a HUB-AND-SPOKE layout: the new platform sits at the centre and every
 * IN-SCOPE system is a spoke whose edge direction comes from its integration direction.
 * Out-of-scope systems are omitted. This module produces a `ResolvedSystemLandscape`
 * directly (no grid topology config), so the same SVG renderer that draws the grid draws
 * this - the web viz and the server SOW PNG stay pixel-identical.
 */
import { SYSTEM_LANDSCAPE_LAYOUT } from './layout.js';
import { TECH_STACK_ROW_MAPPING, resolvePlanTone, PLAN_LABELS } from './config.js';
/** Diagram id of the central platform (new-system) hub node. */
export const TECH_STACK_HUB_ID = '__hub';
/**
 * Hub label when the caller names no platform. Deliberately not a product name: a product's
 * diagram must name its own technology (`landscapeHubNameForProduct`), never borrow another's.
 */
export const DEFAULT_LANDSCAPE_HUB_NAME = 'New platform';
/** Default effort for a Standard integration; the form prefills this (overridable). */
export const TECH_STACK_STANDARD_INTEGRATION_HOURS = 8;
export const TECH_STACK_PLANS = ['keep', 'sunset', 'tbd'];
export const INTEGRATION_DIRECTIONS = [
    'inbound',
    'outbound',
    'two_way',
];
/** Canonical category list, derived from the single mapping in `config.ts`. */
export const TECH_STACK_CATEGORIES = TECH_STACK_ROW_MAPPING.map((row) => ({ id: row.categoryId, label: row.label }));
const CATEGORY_LABEL_BY_ID = new Map(TECH_STACK_CATEGORIES.map((c) => [c.id, c.label]));
const CATEGORY_ORDER = new Map(TECH_STACK_CATEGORIES.map((c, i) => [c.id, i]));
export function techStackCategoryLabel(categoryId) {
    return CATEGORY_LABEL_BY_ID.get(categoryId) ?? categoryId;
}
export function planLabel(plan) {
    return PLAN_LABELS[plan] ?? plan;
}
const DIRECTION_LABELS = {
    inbound: 'Inbound',
    outbound: 'Outbound',
    two_way: 'Two-way',
};
export function integrationDirectionLabel(direction) {
    return DIRECTION_LABELS[direction] ?? direction;
}
/** Keep -> in scope by default; Sunset / TBD -> out of scope by default. User can override. */
export function defaultInScopeForPlan(plan) {
    return plan === 'keep';
}
function isTechStackPlan(value) {
    return value === 'keep' || value === 'sunset' || value === 'tbd';
}
function isDirection(value) {
    return value === 'inbound' || value === 'outbound' || value === 'two_way';
}
function coerceSystem(raw, index) {
    if (!raw || typeof raw !== 'object')
        return null;
    const obj = raw;
    // Store the raw string (no trim): the web editor is stateless and re-derives each
    // input's controlled value from this on every keystroke, so trimming here would strip
    // a trailing space the moment it is typed. Emptiness is guarded downstream via
    // `s.name.trim()` (see `named` / `inScopeIntegrations`).
    const name = typeof obj.name === 'string' ? obj.name : '';
    const category = typeof obj.category === 'string' ? obj.category : '';
    // Keep entries with an empty name: a freshly-added (not-yet-typed) row must survive the
    // round-trip so the editor can render it. Downstream consumers (estimate/diagram/SOW) skip
    // unnamed systems via `inScopeIntegrations` / a name check.
    if (!category)
        return null;
    const plan = isTechStackPlan(obj.plan) ? obj.plan : 'tbd';
    const inScope = typeof obj.inScope === 'boolean' ? obj.inScope : defaultInScopeForPlan(plan);
    const id = typeof obj.id === 'string' && obj.id.trim() ? obj.id : `${category}-${index}-${name}`;
    let integration;
    if (inScope && obj.integration && typeof obj.integration === 'object') {
        const ig = obj.integration;
        const type = ig.type === 'custom' ? 'custom' : 'standard';
        const hoursRaw = typeof ig.hours === 'number' ? ig.hours : Number(ig.hours);
        const hours = Number.isFinite(hoursRaw)
            ? Math.max(0, hoursRaw)
            : type === 'standard'
                ? TECH_STACK_STANDARD_INTEGRATION_HOURS
                : 0;
        const direction = isDirection(ig.direction) ? ig.direction : 'inbound';
        // Drop whitespace-only notes, but keep the raw value (incl. a trailing space the user
        // is mid-typing) - the editor round-trips through this on every keystroke.
        const notes = typeof ig.notes === 'string' && ig.notes.trim() ? ig.notes : undefined;
        const flowTarget = typeof ig.flowTarget === 'string' && ig.flowTarget.trim() ? ig.flowTarget : undefined;
        integration = { type, hours, direction, notes, flowTarget };
    }
    return { id, category, name, plan, inScope, integration };
}
/**
 * Parse a stored `tech_stack_systems` answer (a JSON array, possibly still a JSON string
 * in `deal_response.value`) into a clean `TechStackSystem[]`. Invalid entries are dropped.
 */
export function parseTechStackSystems(value) {
    let arr = value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return [];
        try {
            arr = JSON.parse(trimmed);
        }
        catch {
            return [];
        }
    }
    if (!Array.isArray(arr))
        return [];
    return arr
        .map((raw, i) => coerceSystem(raw, i))
        .filter((s) => s !== null);
}
/** The named, in-scope systems that carry an integration, in canonical category order. */
export function inScopeIntegrations(systems) {
    return systems
        .filter((s) => s.inScope && s.integration && s.name.trim().length > 0)
        .sort((a, b) => (CATEGORY_ORDER.get(a.category) ?? 99) - (CATEGORY_ORDER.get(b.category) ?? 99) ||
        a.name.localeCompare(b.name));
}
/**
 * Seed starter `TechStackSystem` entries from the legacy per-category `tech_stack_<cat>_tool`
 * / `_plan` answers (which AI discovery still populates). One entry per tool found in a
 * category (a category tool answer may be a comma list or an array). Used to scaffold the
 * structured field the first time, then the user owns it (confirm-or-override).
 */
export function seedTechStackSystemsFromLegacy(source) {
    const out = [];
    for (const row of TECH_STACK_ROW_MAPPING) {
        const rawTools = source.getRawValue(row.toolKey);
        const tools = Array.isArray(rawTools)
            ? rawTools.filter((v) => typeof v === 'string' && v.trim().length > 0)
            : typeof rawTools === 'string' && rawTools.trim()
                ? rawTools
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean)
                : [];
        if (tools.length === 0)
            continue;
        const rawPlan = source.getRawValue(row.planKey);
        const plan = isTechStackPlan(rawPlan) ? rawPlan : 'tbd';
        tools.forEach((value, i) => {
            const name = source.getOptionLabel(row.toolKey, value) ?? value;
            const inScope = defaultInScopeForPlan(plan);
            out.push({
                id: `${row.categoryId}-${i}-${value}`,
                category: row.categoryId,
                name,
                plan,
                inScope,
                integration: inScope
                    ? {
                        type: 'standard',
                        hours: TECH_STACK_STANDARD_INTEGRATION_HOURS,
                        direction: 'inbound',
                    }
                    : undefined,
            });
        });
    }
    return out;
}
// ── Current → future column landscape ───────────────────────────────────────────
const L = SYSTEM_LANDSCAPE_LAYOUT;
const NODE_W = L.regularNodeWidth; // 176
const NODE_H = L.regularNodeHeight; // 96
const HUB_W = L.erpNodeWidth; // 208
const HUB_H = 124;
const COL_GAP = L.columnGap; // 100 - between the side columns and the centre region
const PAD_X = L.canvasPaddingX; // 36
const HEADER_TOP = 18;
const HEADER_HEIGHT = 18;
const TOP_PAD = 52; // content start - leaves room for the column header band
const BOTTOM_PAD = 28;
const NODE_GAP = 20; // vertical gap inside the left/right stacks
const ROW_GAP = 24; // horizontal gap between centre-row techs
const CENTER_GAP = 48; // vertical gap between the hub and the centre rows (room for the arrows)
const HUB_ID = TECH_STACK_HUB_ID;
// Category -> column + within-column order. Mirrors the original grid grouping.
const LEFT_CATEGORIES = ['crm', 'billing-engines'];
const CENTER_ABOVE_CATEGORIES = ['erp']; // ABOVE the hub (the system being replaced)
const CENTER_BELOW_CATEGORIES = ['taxes', 'bi-analytics']; // BELOW the hub
const RIGHT_CATEGORIES = ['payroll', 'ap-automation'];
function techStackNode(system, x, y, categoryLabels) {
    return {
        id: system.id,
        x,
        y,
        width: NODE_W,
        height: NODE_H,
        kind: 'regular',
        variant: 'default',
        label: categoryLabels?.[system.category] ?? techStackCategoryLabel(system.category),
        currentTool: system.name,
        postPlan: planLabel(system.plan),
        tone: resolvePlanTone(system.plan, planLabel(system.plan)),
        embeddedChild: null,
    };
}
/**
 * Build a 3-column "current -> future" systems landscape from the structured systems.
 *
 * - Columns: Front Office (CRM, Billing Engines) | Platform (ERP, Taxes, BI + the new-platform hub)
 *   | Downstream (Payroll, AP Automation). One node PER technology, grouped by category in its column.
 * - EVERY named system is drawn, coloured by plan (Keep=green, Sunset=red, TBD=gray), so the figure
 *   reads as the whole current stack and what stays vs sunsets.
 * - The new platform (`hubName`, e.g. "Rillet") is a central node. Each IN-SCOPE system draws one
 *   edge to the hub using its integration direction (inbound=system->hub, outbound=hub->system,
 *   two_way=bidirectional). Sunset / out-of-scope systems get a node but NO edge.
 *
 * Returns an empty layout (no nodes) when there are no named systems, so callers omit the figure.
 */
export function buildTechStackLandscape(systems, options = {}) {
    const hubName = options.hubName?.trim() || DEFAULT_LANDSCAPE_HUB_NAME;
    const named = systems.filter((s) => s.name.trim().length > 0);
    const empty = {
        canvasWidth: 832,
        canvasHeight: TOP_PAD + HUB_H + BOTTOM_PAD,
        headerTop: HEADER_TOP,
        headerHeight: HEADER_HEIGHT,
        columns: [],
        nodes: [],
        edges: [],
    };
    if (named.length === 0)
        return empty;
    const byCat = new Map();
    for (const s of named) {
        const list = byCat.get(s.category) ?? [];
        list.push(s);
        byCat.set(s.category, list);
    }
    const inCats = (cats) => cats.flatMap((c) => byCat.get(c) ?? []);
    const placement = options.placement ?? {
        left: LEFT_CATEGORIES,
        above: CENTER_ABOVE_CATEGORIES,
        below: CENTER_BELOW_CATEGORIES,
        right: RIGHT_CATEGORIES,
    };
    const labels = options.categoryLabels;
    // Any system in an unknown category falls into the left column so nothing is dropped.
    const knownCats = new Set([
        ...placement.left,
        ...placement.above,
        ...placement.below,
        ...placement.right,
    ]);
    const left = [...inCats(placement.left), ...named.filter((s) => !knownCats.has(s.category))];
    const centerAbove = inCats(placement.above);
    const centerBelow = inCats(placement.below);
    const right = inCats(placement.right);
    const colHeight = (n) => (n ? n * NODE_H + (n - 1) * NODE_GAP : 0);
    const rowWidth = (n) => (n ? n * NODE_W + (n - 1) * ROW_GAP : 0);
    const aboveH = centerAbove.length ? NODE_H : 0;
    const belowH = centerBelow.length ? NODE_H : 0;
    const centerColH = aboveH + (aboveH ? CENTER_GAP : 0) + HUB_H + (belowH ? CENTER_GAP : 0) + belowH;
    const contentH = Math.max(colHeight(left.length), colHeight(right.length), centerColH, HUB_H);
    const canvasHeight = TOP_PAD + contentH + BOTTOM_PAD;
    // The centre region is as wide as the widest of hub / above-row / below-row, so many centre
    // techs widen the canvas (pushing the side columns out) instead of overlapping them.
    const centerRegionW = Math.max(HUB_W, rowWidth(centerAbove.length), rowWidth(centerBelow.length));
    const leftX = PAD_X;
    const centerRegionX = leftX + NODE_W + COL_GAP;
    const hubX = centerRegionX + (centerRegionW - HUB_W) / 2;
    const rightX = centerRegionX + centerRegionW + COL_GAP;
    const canvasWidth = rightX + NODE_W + PAD_X;
    const centerColTop = TOP_PAD + (contentH - centerColH) / 2;
    const aboveRowY = centerColTop;
    const hubY = centerColTop + aboveH + (aboveH ? CENTER_GAP : 0);
    const belowRowY = hubY + HUB_H + (belowH ? CENTER_GAP : 0);
    const nodes = [];
    const edges = [];
    const edgeSpecs = [];
    // Centre rows: techs side by side (NOT stacked), so no edge ever routes through another card.
    const placeRow = (row, rowY, side) => {
        const techHandle = side === 'above' ? 'bottom' : 'top';
        let x = centerRegionX + (centerRegionW - rowWidth(row.length)) / 2;
        for (const system of row) {
            nodes.push(techStackNode(system, x, rowY, labels));
            if (system.inScope && system.integration) {
                const techPoint = { x: x + NODE_W / 2, y: side === 'above' ? rowY + NODE_H : rowY };
                edgeSpecs.push({ system, side, techHandle, techPoint });
            }
            x += NODE_W + ROW_GAP;
        }
    };
    placeRow(centerAbove, aboveRowY, 'above');
    nodes.push({
        id: HUB_ID,
        x: hubX,
        y: hubY,
        width: HUB_W,
        height: HUB_H,
        kind: 'regular',
        variant: 'default',
        label: 'Platform',
        currentTool: hubName,
        postPlan: null,
        tone: 'neutral',
        embeddedChild: null,
    });
    placeRow(centerBelow, belowRowY, 'below');
    // Side columns: vertical stacks, centred on the canvas content.
    const placeSide = (col, x, side) => {
        const techHandle = side === 'left' ? 'right' : 'left';
        let y = TOP_PAD + (contentH - colHeight(col.length)) / 2;
        for (const system of col) {
            nodes.push(techStackNode(system, x, y, labels));
            if (system.inScope && system.integration) {
                const techPoint = { x: side === 'left' ? x + NODE_W : x, y: y + NODE_H / 2 };
                edgeSpecs.push({ system, side, techHandle, techPoint });
            }
            y += NODE_H + NODE_GAP;
        }
    };
    placeSide(left, leftX, 'left');
    placeSide(right, rightX, 'right');
    const rectById = new Map(nodes.map((n) => [n.id, { x: n.x, y: n.y, w: n.width, h: n.height }]));
    const centerOf = (r) => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
    // A flowTarget only counts when it names a different, placed node; otherwise fall back to hub.
    const resolveTargetId = (s) => {
        const ft = s.integration?.flowTarget;
        return ft && ft !== s.id && ft !== HUB_ID && rectById.has(ft) ? ft : HUB_ID;
    };
    // Emit one edge, orienting source/target by integration direction
    // (inbound/two_way: system -> other; outbound: other -> system).
    const pushEdge = (system, otherId, systemHandle, systemPoint, otherHandle, otherPoint) => {
        const dir = system.integration.direction;
        if (dir === 'outbound') {
            edges.push({
                id: `edge-${system.id}`,
                source: otherId,
                target: system.id,
                sourceHandle: otherHandle,
                targetHandle: systemHandle,
                sourcePoint: otherPoint,
                targetPoint: systemPoint,
                bidirectional: false,
            });
        }
        else {
            edges.push({
                id: `edge-${system.id}`,
                source: system.id,
                target: otherId,
                sourceHandle: systemHandle,
                targetHandle: otherHandle,
                sourcePoint: systemPoint,
                targetPoint: otherPoint,
                bidirectional: dir === 'two_way',
            });
        }
    };
    // ── Hub-bound edges: FAN each side's attach points along the hub edge (unchanged). ──
    const hubSpecs = edgeSpecs.filter((s) => resolveTargetId(s.system) === HUB_ID);
    const hubHandleFor = (side) => side === 'left' ? 'left' : side === 'right' ? 'right' : side === 'above' ? 'top' : 'bottom';
    const hubPointFor = (side, i, n) => {
        const t = (i + 1) / (n + 1);
        if (side === 'left')
            return { x: hubX, y: hubY + HUB_H * t };
        if (side === 'right')
            return { x: hubX + HUB_W, y: hubY + HUB_H * t };
        if (side === 'above')
            return { x: hubX + HUB_W * t, y: hubY };
        return { x: hubX + HUB_W * t, y: hubY + HUB_H };
    };
    for (const side of ['left', 'right', 'above', 'below']) {
        const list = hubSpecs.filter((s) => s.side === side);
        list.sort((a, b) => side === 'left' || side === 'right'
            ? a.techPoint.y - b.techPoint.y
            : a.techPoint.x - b.techPoint.x);
        list.forEach((spec, i) => {
            pushEdge(spec.system, HUB_ID, spec.techHandle, spec.techPoint, hubHandleFor(side), hubPointFor(side, i, list.length));
        });
    }
    // ── System -> system edges: attach on each node's facing side, fanned per (node, side). ──
    const facingSide = (from, to) => {
        const a = centerOf(from);
        const b = centerOf(to);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        if (Math.abs(dx) >= Math.abs(dy))
            return dx >= 0 ? 'right' : 'left';
        return dy >= 0 ? 'bottom' : 'top';
    };
    const attachPoint = (r, side, i, n) => {
        const t = (i + 1) / (n + 1);
        if (side === 'left')
            return { x: r.x, y: r.y + r.h * t };
        if (side === 'right')
            return { x: r.x + r.w, y: r.y + r.h * t };
        if (side === 'top')
            return { x: r.x + r.w * t, y: r.y };
        return { x: r.x + r.w * t, y: r.y + r.h };
    };
    const links = edgeSpecs
        .filter((s) => resolveTargetId(s.system) !== HUB_ID)
        .map((spec) => {
        const targetId = resolveTargetId(spec.system);
        const sysRect = rectById.get(spec.system.id);
        const tgtRect = rectById.get(targetId);
        const sysSide = facingSide(sysRect, tgtRect);
        const tgtSide = facingSide(tgtRect, sysRect);
        const sysEnd = {
            key: `${spec.system.id}|${sysSide}`,
            nodeId: spec.system.id,
            side: sysSide,
            other: centerOf(tgtRect),
        };
        const tgtEnd = {
            key: `${targetId}|${tgtSide}`,
            nodeId: targetId,
            side: tgtSide,
            other: centerOf(sysRect),
        };
        return { spec, targetId, sysEnd, tgtEnd };
    });
    // Fan every endpoint that lands on the same node + side so lines don't overlap.
    const endsByKey = new Map();
    for (const l of links) {
        for (const end of [l.sysEnd, l.tgtEnd]) {
            const arr = endsByKey.get(end.key) ?? [];
            arr.push(end);
            endsByKey.set(end.key, arr);
        }
    }
    for (const arr of endsByKey.values()) {
        const first = arr[0];
        if (!first)
            continue;
        const side = first.side;
        arr.sort((p, q) => side === 'left' || side === 'right' ? p.other.y - q.other.y : p.other.x - q.other.x);
        arr.forEach((end, i) => {
            end.point = attachPoint(rectById.get(end.nodeId), side, i, arr.length);
        });
    }
    for (const l of links) {
        pushEdge(l.spec.system, l.targetId, l.sysEnd.side, l.sysEnd.point, l.tgtEnd.side, l.tgtEnd.point);
    }
    return {
        canvasWidth,
        canvasHeight,
        headerTop: HEADER_TOP,
        headerHeight: HEADER_HEIGHT,
        columns: [
            { id: 'left', label: 'Front Office', x: leftX, width: NODE_W },
            { id: 'center', label: 'Platform', x: centerRegionX, width: centerRegionW },
            { id: 'right', label: 'Downstream', x: rightX, width: NODE_W },
        ],
        nodes,
        edges,
    };
}
//# sourceMappingURL=tech-stack.js.map